import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useSuperadminCheck } from "../hooks/useSuperadmin";
import type { OrgTier } from "@wondermaps/db";

export type { OrgTier };

// Types for org data
export type OrgType = "zoo" | "garden" | "fair" | "park" | "default";

interface Organization {
  id: string;
  name: string;
  slug: string;
  type: OrgType;
  tier: OrgTier;
  settings?: Record<string, unknown> | null;
  role: string;
}

interface MapInfo {
  id: string;
  name: string;
  slug: string; // map slug for map routes
  description: string | null;
  isDefault: boolean;
  createdAt: string;
  status: "draft" | "published" | null;
  updatedAt: string | null;
  isScheduleActive: boolean;
  scheduleStartDate: string | null;
  scheduleEndDate: string | null;
  scheduleStartTime: string | null;
  scheduleEndTime: string | null;
  scheduleDaysOfWeek: number[] | null;
  eventPeriodName: string | null;
  eventPeriodStart: string | null;
  eventPeriodEnd: string | null;
  mapSize: "small" | "medium" | "large";
  operatingMode: "always_open" | "year_round" | "seasonal";
  operatingConfig: import("@wondermaps/maps").OperatingConfig | null;
  operatingMessages: import("@wondermaps/db/schema").OperatingStatusMessages | null;
  shareImagePublicId: string | null;
  shareImageVersion: string | null;
}

interface OrgContextValue {
  org: Organization | null;
  orgId: string;
  orgSlug: string;
  allOrgs: Organization[];
  primaryMap: MapInfo | null;
  allMaps: MapInfo[];
  isLoading: boolean;
  error: string | null;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error("useOrgContext must be used within an OrgProvider");
  }
  return ctx;
}

interface OrgProviderProps {
  children: ReactNode;
  orgSlug: string;
}

export function OrgProvider({ children, orgSlug }: OrgProviderProps) {
  // Check if user is superadmin
  const { data: superadminData, isLoading: isSuperadminLoading } = useSuperadminCheck();
  const isSuperadmin = superadminData?.isSuperadmin ?? false;

  // Fetch user's organizations
  const orgsQuery = useQuery({
    queryKey: ["orgs", "me"],
    queryFn: async () => {
      const res = await api.orgs.me.$get({});
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json();
    },
  });

  // Fetch all orgs for superadmins (fallback when not a member)
  const adminOrgsQuery = useQuery({
    queryKey: ["superadmin", "orgs"],
    queryFn: async () => {
      const res = await api.orgs.admin.$get();
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json() as Promise<{
        orgs: Array<{
          id: string;
          name: string;
          slug: string;
          type: string | null;
          tier: string;
          settings?: Record<string, unknown> | null;
          memberCount: number;
          createdAt: string;
        }>;
      }>;
    },
    enabled: isSuperadmin,
  });

  // Find org matching URL slug - check user's orgs first, then superadmin list
  const userOrgs = orgsQuery.data?.organizations ?? [];
  const adminOrgs = adminOrgsQuery.data?.orgs ?? [];

  // Build combined org list and find current org
  const { allOrgs, org, accessDenied } = useMemo(() => {
    // Build merged list: user orgs + superadmin-only orgs (not already in userOrgs)
    const userOrgIds = new Set(userOrgs.map((o) => o.id));
    const superadminOnlyOrgs: Organization[] =
      isSuperadmin
        ? adminOrgs
            .filter((o) => !userOrgIds.has(o.id))
            .map((o) => ({
              id: o.id,
              name: o.name,
              slug: o.slug,
              type: (o.type ?? "default") as OrgType,
              tier: (o.tier ?? "base") as OrgTier,
              settings: o.settings,
              role: "superadmin",
            }))
        : [];
    const merged = [...userOrgs, ...superadminOnlyOrgs];

    // Find current org from merged list
    const currentOrg = merged.find((o) => o.slug === orgSlug);
    if (currentOrg) {
      return { allOrgs: merged, org: currentOrg, accessDenied: false };
    }

    // No access
    const denied = orgsQuery.data !== undefined && (isSuperadmin ? adminOrgsQuery.data !== undefined : true);
    return { allOrgs: merged, org: null, accessDenied: denied };
  }, [userOrgs, adminOrgs, orgSlug, isSuperadmin, orgsQuery.data, adminOrgsQuery.data]);

  // Fetch org's maps using the slug-based endpoint
  const mapsQuery = useQuery({
    queryKey: ["maps", orgSlug, "all"],
    queryFn: async () => {
      const res = await api.maps[":slug"].all.$get({
        param: { slug: orgSlug },
      });
      if (!res.ok) throw new Error("Failed to fetch maps");
      return res.json();
    },
    enabled: !!org,
  });

  // Build all maps with proper MapInfo structure
  // Fallback slug generation for maps without slugs (backward compat)
  const slugify = (name: string) => name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  const allMaps: MapInfo[] = (mapsQuery.data?.maps ?? []).map((m) => ({
    id: m.id,
    name: m.name,
    slug: m.slug || slugify(m.name), // use map's slug or generate from name
    description: m.description,
    isDefault: m.isDefault ?? false,
    createdAt: m.createdAt,
    status: m.versions?.find((v) => v.status === "published")
      ? "published"
      : m.versions?.[0]?.status ?? null,
    updatedAt: m.versions?.[0]?.createdAt ?? null,
    isScheduleActive: m.isScheduleActive ?? true,
    scheduleStartDate: m.scheduleStartDate ?? null,
    scheduleEndDate: m.scheduleEndDate ?? null,
    scheduleStartTime: m.scheduleStartTime ?? null,
    scheduleEndTime: m.scheduleEndTime ?? null,
    scheduleDaysOfWeek: m.scheduleDaysOfWeek ?? null,
    eventPeriodName: m.eventPeriodName ?? null,
    eventPeriodStart: m.eventPeriodStart ?? null,
    eventPeriodEnd: m.eventPeriodEnd ?? null,
    mapSize: (m.mapSize as "small" | "medium" | "large") ?? "small",
    operatingMode: (m.operatingMode as "always_open" | "year_round" | "seasonal") ?? "year_round",
    operatingConfig: (m.operatingConfig as import("@wondermaps/maps").OperatingConfig | null) ?? null,
    operatingMessages: (m.operatingMessages as import("@wondermaps/db/schema").OperatingStatusMessages | null) ?? null,
    shareImagePublicId: m.shareImagePublicId ?? null,
    shareImageVersion: m.shareImageVersion ?? null,
  }));

  // Primary map is first map
  const primaryMap = allMaps[0] ?? null;

  // Include superadmin check in loading — prevents access-denied / empty-state /
  // read-only banner flashes while the fallback admin query hasn't kicked in yet.
  const isLoading = isSuperadminLoading || orgsQuery.isLoading || (isSuperadmin && adminOrgsQuery.isLoading) || (!!org && mapsQuery.isLoading);
  const error = accessDenied ? "Access denied to this organization" : null;

  const value: OrgContextValue = {
    org,
    orgId: org?.id ?? "",
    orgSlug,
    allOrgs,
    primaryMap,
    allMaps,
    isLoading,
    error,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export { OrgContext };
export type { MapInfo };
